import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Calendar, MapPin, HelpCircle, Zap, Users, ChevronDown, ChevronUp, Globe, Wifi } from 'lucide-react';
import { eventsAPI, modelOptionsAPI } from '../utils/api';
import { useData } from '../contexts/DataContext';

const LOCATION_TYPE_OPTIONS = [
  { value: 'physical', label: 'Physical location' },
  { value: 'online',   label: 'Online / Digital' },
  { value: 'unknown',  label: 'Unknown' },
];

const EMPTY_FORM = {
  title: '',
  event_type: '',
  description: '',
  event_date: '',
  end_date: '',
  date_is_approximate: false,
  date_range_start: '',
  date_range_end: '',
  location_type: 'physical',
  location_name: '',
  address: '',
  city: '',
  country: '',
  latitude: '',
  longitude: '',
  online_platform: '',
  how_method: '',
  why_context: '',
  case_id: '',
  participants: [],
};

const formatDate = (d) => {
  if (!d) return null;
  return new Date(d).toLocaleString('th-TH', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const EventCard = ({ event, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <div
        className="flex items-start justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 dark:text-slate-100">{event.title}</span>
            {event.event_type && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                {event.event_type}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-slate-400 flex-wrap">
            {event.event_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(event.event_date)}
                {event.date_is_approximate && <span className="text-xs">(approx)</span>}
              </span>
            )}
            {!event.event_date && event.date_range_start && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(event.date_range_start)} – {event.date_range_end ? formatDate(event.date_range_end) : '?'}
              </span>
            )}
            {event.location_type === 'physical' && event.city && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {[event.location_name, event.city, event.country].filter(Boolean).join(', ')}
              </span>
            )}
            {event.location_type === 'online' && (
              <span className="flex items-center gap-1">
                <Wifi className="w-3.5 h-3.5" />
                {event.online_platform || 'Online'}
              </span>
            )}
            {event.participants?.length > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {event.participants.length} participant{event.participants.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 ml-2 shrink-0">
          <button onClick={e => { e.stopPropagation(); onEdit(event); }}
            className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={e => { e.stopPropagation(); onDelete(event.id); }}
            className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded">
            <Trash2 className="w-4 h-4" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-slate-700 space-y-3 pt-3">
          {event.description && (
            <div>
              <div className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">WHAT</div>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{event.description}</p>
            </div>
          )}
          {event.location_type === 'physical' && (event.location_name || event.address) && (
            <div>
              <div className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">WHERE</div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {[event.location_name, event.address, event.city, event.country].filter(Boolean).join(', ')}
              </p>
            </div>
          )}
          {event.how_method && (
            <div>
              <div className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">HOW</div>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{event.how_method}</p>
            </div>
          )}
          {event.why_context && (
            <div>
              <div className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">WHY</div>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{event.why_context}</p>
            </div>
          )}
          {event.participants?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">WHO</div>
              <div className="flex flex-wrap gap-2">
                {event.participants.map(p => (
                  <span key={p.id} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                    p.entity_type === 'business'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                  }`}>
                    {p.entity_name}
                    {p.role && <span className="opacity-70">· {p.role}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EventForm = ({ initial, currentPersonId, eventTypes, participantRoles, people, businesses, onSave, onCancel }) => {
  const [form, setForm] = useState(() => {
    if (initial) {
      return {
        ...EMPTY_FORM,
        ...initial,
        event_date: initial.event_date ? initial.event_date.slice(0, 16) : '',
        end_date: initial.end_date ? initial.end_date.slice(0, 16) : '',
        date_range_start: initial.date_range_start ? initial.date_range_start.slice(0, 16) : '',
        date_range_end: initial.date_range_end ? initial.date_range_end.slice(0, 16) : '',
        participants: initial.participants || [],
      };
    }
    return {
      ...EMPTY_FORM,
      participants: [{ entity_id: currentPersonId, entity_type: 'person', role: '', notes: '' }],
    };
  });
  const [saving, setSaving] = useState(false);
  const [participantSearch, setParticipantSearch] = useState('');
  const [showParticipantPicker, setShowParticipantPicker] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addParticipant = (entityId, entityType, name) => {
    const already = form.participants.some(p => p.entity_id === entityId && p.entity_type === entityType);
    if (!already) {
      setForm(f => ({ ...f, participants: [...f.participants, { entity_id: entityId, entity_type: entityType, role: '', notes: '', _name: name }] }));
    }
    setParticipantSearch('');
    setShowParticipantPicker(false);
  };

  const removeParticipant = (idx) => setForm(f => ({ ...f, participants: f.participants.filter((_, i) => i !== idx) }));
  const updateParticipant = (idx, field, value) => setForm(f => {
    const next = [...f.participants];
    next[idx] = { ...next[idx], [field]: value };
    return { ...f, participants: next };
  });

  const handleSave = async () => {
    if (!form.title.trim()) return alert('Title is required');
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const inp = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-gray-100';
  const label = 'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1';

  const allEntities = [
    ...people.map(p => ({ id: p.id, type: 'person', name: `${p.first_name} ${p.last_name || ''}`.trim(), sub: p.category })),
    ...businesses.map(b => ({ id: b.id, type: 'business', name: b.name, sub: 'Business' })),
  ].filter(e => {
    const search = participantSearch.toLowerCase();
    return !search || e.name.toLowerCase().includes(search);
  }).filter(e => !form.participants.some(p => p.entity_id === e.id && p.entity_type === e.type));

  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-slate-100">{initial ? 'Edit Event' : 'New Event'}</h3>
        <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="w-4 h-4" /></button>
      </div>

      {/* Title + Type */}
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className={label}>Title *</label>
          <input className={inp} value={form.title} onChange={e => set('title', e.target.value)} placeholder="Event title" />
        </div>
        <div>
          <label className={label}>Type</label>
          <select className={inp} value={form.event_type} onChange={e => set('event_type', e.target.value)}>
            <option value="">— select —</option>
            {eventTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      {/* WHAT */}
      <div>
        <label className={label}><Zap className="inline w-3 h-3 mr-1" />WHAT — description</label>
        <textarea className={inp} rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What happened?" />
      </div>

      {/* WHEN */}
      <div>
        <label className={label}><Calendar className="inline w-3 h-3 mr-1" />WHEN</label>
        <div className="flex items-center gap-2 mb-2">
          <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
            <input type="checkbox" checked={form.date_is_approximate} onChange={e => set('date_is_approximate', e.target.checked)} className="rounded" />
            Approximate / range
          </label>
        </div>
        {!form.date_is_approximate ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">Start</label>
              <input type="datetime-local" className={inp} value={form.event_date} onChange={e => set('event_date', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">End (optional)</label>
              <input type="datetime-local" className={inp} value={form.end_date} onChange={e => set('end_date', e.target.value)} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">From (approx)</label>
              <input type="datetime-local" className={inp} value={form.date_range_start} onChange={e => set('date_range_start', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-gray-500 dark:text-slate-400 mb-1 block">To (approx)</label>
              <input type="datetime-local" className={inp} value={form.date_range_end} onChange={e => set('date_range_end', e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* WHERE */}
      <div>
        <label className={label}><MapPin className="inline w-3 h-3 mr-1" />WHERE</label>
        <div className="flex gap-2 mb-2">
          {LOCATION_TYPE_OPTIONS.map(opt => (
            <label key={opt.value} className={`flex items-center gap-1.5 text-sm cursor-pointer px-3 py-1.5 rounded-lg border transition-all ${
              form.location_type === opt.value
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : 'border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
            }`}>
              <input type="radio" name="loc_type" value={opt.value} checked={form.location_type === opt.value}
                onChange={() => set('location_type', opt.value)} className="sr-only" />
              {opt.label}
            </label>
          ))}
        </div>
        {form.location_type === 'physical' && (
          <div className="space-y-2">
            <input className={inp} value={form.location_name} onChange={e => set('location_name', e.target.value)} placeholder="Location name (e.g. Coffee shop)" />
            <input className={inp} value={form.address} onChange={e => set('address', e.target.value)} placeholder="Address" />
            <div className="grid grid-cols-2 gap-2">
              <input className={inp} value={form.city} onChange={e => set('city', e.target.value)} placeholder="City" />
              <input className={inp} value={form.country} onChange={e => set('country', e.target.value)} placeholder="Country" />
            </div>
          </div>
        )}
        {form.location_type === 'online' && (
          <input className={inp} value={form.online_platform} onChange={e => set('online_platform', e.target.value)} placeholder="Platform (e.g. Zoom, Line, Telegram)" />
        )}
      </div>

      {/* HOW */}
      <div>
        <label className={label}><Globe className="inline w-3 h-3 mr-1" />HOW — method / means</label>
        <textarea className={inp} rows={2} value={form.how_method} onChange={e => set('how_method', e.target.value)} placeholder="How did it happen?" />
      </div>

      {/* WHY */}
      <div>
        <label className={label}><HelpCircle className="inline w-3 h-3 mr-1" />WHY — context / motivation</label>
        <textarea className={inp} rows={2} value={form.why_context} onChange={e => set('why_context', e.target.value)} placeholder="Why / motivation / context" />
      </div>

      {/* WHO — participants */}
      <div>
        <label className={label}><Users className="inline w-3 h-3 mr-1" />WHO — participants</label>
        <div className="space-y-2 mb-2">
          {form.participants.map((p, idx) => {
            const name = p._name || people.find(x => x.id === p.entity_id && p.entity_type === 'person')
              ? `${people.find(x => x.id === p.entity_id)?.first_name || ''} ${people.find(x => x.id === p.entity_id)?.last_name || ''}`.trim()
              : businesses.find(x => x.id === p.entity_id)?.name || `Entity #${p.entity_id}`;
            return (
              <div key={idx} className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                  p.entity_type === 'business'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                }`}>{name || p._name}</span>
                <select value={p.role || ''} onChange={e => updateParticipant(idx, 'role', e.target.value)}
                  className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-gray-100">
                  <option value="">— role —</option>
                  {participantRoles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                {!(p.entity_id === currentPersonId && p.entity_type === 'person') && (
                  <button onClick={() => removeParticipant(idx)} className="p-1 text-red-400 hover:text-red-600 shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="relative">
          <input
            type="text"
            value={participantSearch}
            onChange={e => { setParticipantSearch(e.target.value); setShowParticipantPicker(true); }}
            onFocus={() => setShowParticipantPicker(true)}
            placeholder="Add participant (search people / businesses)..."
            className={inp}
          />
          {showParticipantPicker && participantSearch && allEntities.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-40 overflow-y-auto">
              {allEntities.slice(0, 10).map(e => (
                <button key={`${e.type}-${e.id}`} type="button"
                  onClick={() => addParticipant(e.id, e.type, e.name)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center justify-between">
                  <span className="text-gray-900 dark:text-slate-100">{e.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    e.type === 'business' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                  }`}>{e.sub || e.type}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-slate-700">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">Cancel</button>
        <button onClick={handleSave} disabled={saving}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving…' : (initial ? 'Update' : 'Create')}
        </button>
      </div>
    </div>
  );
};

const EventsTab = ({ personId }) => {
  const { people, businesses } = useData();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [participantRoles, setParticipantRoles] = useState([]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await eventsAPI.getByEntity(personId, 'person');
      setEvents(data);
    } catch (e) {
      console.error('Failed to load events', e);
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    loadEvents();
    modelOptionsAPI.getAll().then(opts => {
      const pick = (type) => opts
        .filter(o => o.model_type === type && o.is_active)
        .sort((a, b) => a.display_order - b.display_order)
        .map(o => ({ value: o.option_value, label: o.option_label }));
      setEventTypes(pick('event_type'));
      setParticipantRoles(pick('participant_role'));
    }).catch(() => {});
  }, [loadEvents]);

  const handleSave = async (formData) => {
    if (editingEvent) {
      await eventsAPI.update(editingEvent.id, formData);
    } else {
      await eventsAPI.create(formData);
    }
    setShowForm(false);
    setEditingEvent(null);
    await loadEvents();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    await eventsAPI.delete(id);
    await loadEvents();
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setShowForm(true);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-gray-400">Loading events…</div>
  );

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-slate-100">
          Events / Incidents
          {events.length > 0 && <span className="ml-2 text-sm font-normal text-gray-400">({events.length})</span>}
        </h3>
        {!showForm && (
          <button onClick={() => { setEditingEvent(null); setShowForm(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus className="w-4 h-4" /> Add Event
          </button>
        )}
      </div>

      {showForm && (
        <EventForm
          initial={editingEvent}
          currentPersonId={personId}
          eventTypes={eventTypes}
          participantRoles={participantRoles}
          people={people}
          businesses={businesses}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingEvent(null); }}
        />
      )}

      {events.length === 0 && !showForm ? (
        <div className="text-center py-12 text-gray-400 dark:text-slate-500">
          No events recorded yet
        </div>
      ) : (
        <div className="space-y-2">
          {events.map(ev => (
            <EventCard key={ev.id} event={ev} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
};

export default EventsTab;
